import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { getTemplate } from "@/lib/templates";
import { formatDate } from "@/lib/format";
import { Heart, ExternalLink, Check, MailOpen, CalendarDays, Instagram, Copy, MessageCircle } from "lucide-react";
import MusicPlayer from "@/components/MusicPlayer";
import TemplateDecor from "@/components/TemplateDecor";
import { getBackgroundUrl } from "@/lib/backgrounds";
import { defaultDesign, mergeDesign } from "@/lib/design";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KET = ["Keluarga", "Teman", "Rekan Kerja", "Tetangga", "Rekan Orang Tua", "Sahabat", "VIP", "Lain-lain"];
const pick = (uploaded, url) => uploaded || url || "";

function useCountdown(dateStr) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0, done: false });
  useEffect(() => {
    if (!dateStr) return;
    const target = new Date(dateStr + "T00:00:00").getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) { setT({ d: 0, h: 0, m: 0, s: 0, done: true }); return; }
      setT({ d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000), done: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  return t;
}

function Unit({ value, label, accent, font }) {
  return (
    <div className="flex flex-col items-center">
      <div className="min-w-[64px] rounded-2xl border bg-white/5 px-3 py-4 backdrop-blur-md sm:min-w-[88px]" style={{ borderColor: `${accent}55` }}>
        <span className="text-4xl font-light tabular-nums sm:text-6xl" style={{ fontFamily: font }}>{String(value).padStart(2, "0")}</span>
      </div>
      <span className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/60 sm:text-xs">{label}</span>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none";

function PhotoBlock({ src, shape }) {
  if (!src) return null;
  if (shape === "circle") return <img src={src} alt="" className="mx-auto h-40 w-40 rounded-full border-2 border-white/30 object-cover shadow-xl sm:h-52 sm:w-52" />;
  return <img src={src} alt="" className="mx-auto max-h-72 w-full max-w-md rounded-2xl border border-white/15 object-cover shadow-xl" />;
}

function downloadICS({ title, dateStr, timeStr, location }) {
  if (!dateStr) return;
  const [y, m, d] = dateStr.split("-");
  const [hh, mm] = (timeStr || "09:00").split(":");
  const start = `${y}${m}${d}T${hh}${mm}00`;
  const endH = String((parseInt(hh, 10) + 2) % 24).padStart(2, "0");
  const end = `${y}${m}${d}T${endH}${mm}00`;
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "BEGIN:VEVENT",
    `SUMMARY:${title || "Wedding"}`,
    `DTSTART:${start}`, `DTEND:${end}`,
    location ? `LOCATION:${location}` : "",
    "END:VEVENT", "END:VCALENDAR"].filter(Boolean).join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${(title || "wedding").replace(/\s+/g, "_")}.ics`;
  a.click();
}

function BrideGroomCard({ side, data, accent, font, label }) {
  const src = pick(data.foto, data.foto_url);
  if (!data.nama_lengkap && !data.bio && !src) return null;
  return (
    <div data-testid={`mempelai-${side}`} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
      {src && <img src={src} alt="" className="mx-auto h-32 w-32 rounded-full border-2 border-white/30 object-cover shadow-lg" />}
      <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/60">{label}</p>
      {data.nama_lengkap && <p className="mt-2 text-3xl font-light" style={{ fontFamily: font, color: accent }}>{data.nama_lengkap}</p>}
      {data.bio && <p className="mt-2 text-sm leading-relaxed text-white/80">{data.bio}</p>}
      {(data.ayah || data.ibu) && (
        <p className="mt-3 text-xs text-white/60">
          Putra/Putri dari <br />
          {data.ayah && <span>Bpk. {data.ayah}</span>}
          {data.ayah && data.ibu && <span> &amp; </span>}
          {data.ibu && <span>Ibu {data.ibu}</span>}
        </p>
      )}
      {data.instagram && (
        <a href={`https://instagram.com/${data.instagram}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs" style={{ borderColor: `${accent}66`, color: accent }}>
          <Instagram className="h-3 w-3" /> @{data.instagram}
        </a>
      )}
    </div>
  );
}

function GiftAccount({ acc, accent }) {
  const copy = async () => { try { await navigator.clipboard.writeText(acc.nomor); toast.success("Nomor disalin"); } catch { toast.info(acc.nomor); } };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-md">
      {acc.label && <p className="text-xs uppercase tracking-[0.3em]" style={{ color: accent }}>{acc.label}</p>}
      {acc.nomor && <p className="mt-2 select-all text-lg tabular-nums text-white/90">{acc.nomor}</p>}
      {acc.atas_nama && <p className="mt-1 text-sm text-white/60">a.n. {acc.atas_nama}</p>}
      {acc.nomor && (
        <button onClick={copy} className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs hover:bg-white/10" style={{ borderColor: `${accent}66`, color: accent }}>
          <Copy className="h-3 w-3" /> Salin Nomor
        </button>
      )}
    </div>
  );
}

function RSVPForm({ pid, accent, initialName, cfg, events }) {
  const [f, setF] = useState({ nama: initialName || "", tamu_dari: "Pria", keterangan: "Teman", kehadiran: "Hadir", jumlah: 1, pesan: "", acara: events[0]?.nama || "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [c1] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [c2] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [cap, setCap] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const showAcara = cfg.field_acara && events.length > 1;
  const submit = async (e) => {
    e.preventDefault();
    if (cfg.field_nama && !f.nama.trim()) { toast.error("Isi nama kamu"); return; }
    if (parseInt(cap, 10) !== c1 + c2) { toast.error("Jawaban verifikasi salah"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/rsvp/${pid}`, { ...f, jumlah: parseInt(f.jumlah, 10) || 1, pesan: showAcara ? `[${f.acara}] ${f.pesan}` : f.pesan });
      setSent(true);
    } catch (err) { toast.error(err.response?.status === 429 ? "Terlalu banyak percobaan. Coba lagi nanti." : "Gagal mengirim konfirmasi. Coba lagi."); setLoading(false); }
  };

  if (sent) return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-white/15 bg-white/5 p-8 text-center backdrop-blur-md">
      <Check className="mx-auto h-10 w-10" style={{ color: accent }} />
      <p className="mt-3 font-heading text-2xl">Terima kasih, {f.nama}!</p>
      <p className="mt-1 text-sm text-white/60">Konfirmasi kehadiranmu sudah kami terima.</p>
    </motion.div>
  );

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/15 bg-white/5 p-6 text-left backdrop-blur-md" data-testid="rsvp-form">
      <p className="mb-4 text-center text-xs uppercase tracking-[0.3em] text-white/60">{cfg.cta_label || "Konfirmasi Kehadiran"}</p>
      <div className="space-y-3">
        {cfg.field_nama && <input data-testid="rsvp-nama" className={inputCls} placeholder="Nama kamu" value={f.nama} onChange={(e) => set("nama", e.target.value)} />}
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="rsvp-dari" className={inputCls} value={f.tamu_dari} onChange={(e) => set("tamu_dari", e.target.value)}>
            <option className="bg-neutral-900" value="Pria">Tamu Pria</option>
            <option className="bg-neutral-900" value="Wanita">Tamu Wanita</option>
          </select>
          <select data-testid="rsvp-ket" className={inputCls} value={f.keterangan} onChange={(e) => set("keterangan", e.target.value)}>
            {KET.map((k) => <option className="bg-neutral-900" key={k} value={k}>{k}</option>)}
          </select>
        </div>
        {showAcara && (
          <select data-testid="rsvp-acara" className={inputCls} value={f.acara} onChange={(e) => set("acara", e.target.value)}>
            {events.map((ev, i) => <option className="bg-neutral-900" key={i} value={ev.nama}>{ev.nama || `Acara ${i + 1}`}</option>)}
          </select>
        )}
        <div className="grid grid-cols-2 gap-3">
          {cfg.field_kehadiran && (
            <select data-testid="rsvp-hadir" className={inputCls} value={f.kehadiran} onChange={(e) => set("kehadiran", e.target.value)}>
              <option className="bg-neutral-900" value="Hadir">Hadir</option>
              <option className="bg-neutral-900" value="Tidak Hadir">Tidak Hadir</option>
            </select>
          )}
          <input data-testid="rsvp-jumlah" type="number" min="1" max={cfg.max_tamu || 2} className={inputCls} placeholder={`Jumlah orang (maks ${cfg.max_tamu || 2})`} value={f.jumlah} onChange={(e) => set("jumlah", e.target.value)} />
        </div>
        {cfg.field_pesan && <textarea data-testid="rsvp-pesan" className={inputCls} rows={2} placeholder="Ucapan / pesan (opsional)" value={f.pesan} onChange={(e) => set("pesan", e.target.value)} />}
        <input data-testid="rsvp-captcha" className={inputCls} inputMode="numeric" placeholder={`Verifikasi: berapa ${c1} + ${c2}?`} value={cap} onChange={(e) => setCap(e.target.value)} />
        <button data-testid="rsvp-submit" type="submit" disabled={loading} style={{ backgroundColor: accent }}
          className="w-full rounded-full py-3 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {loading ? "Mengirim..." : (cfg.cta_label || "Kirim Konfirmasi")}
        </button>
      </div>
    </form>
  );
}

function GuestbookSection({ pid, accent, font }) {
  const [list, setList] = useState([]);
  const [nama, setNama] = useState("");
  const [pesan, setPesan] = useState("");
  const [c1] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [c2] = useState(() => Math.floor(Math.random() * 8) + 1);
  const [cap, setCap] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/public/guestbook/${pid}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const submit = async (e) => {
    e.preventDefault();
    if (!nama.trim() || !pesan.trim()) { toast.error("Isi nama & ucapan"); return; }
    if (parseInt(cap, 10) !== c1 + c2) { toast.error("Jawaban verifikasi salah"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/guestbook/${pid}`, { nama, pesan });
      setNama(""); setPesan(""); setCap("");
      toast.success("Ucapan terkirim, terima kasih!");
      load();
    } catch (err) { toast.error(err.response?.status === 429 ? "Terlalu banyak percobaan. Coba lagi nanti." : "Gagal mengirim ucapan."); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }} className="mt-14 w-full max-w-md" data-testid="guestbook-section">
      <p className="mb-4 flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.3em] text-white/60"><MessageCircle className="h-3.5 w-3.5" /> Buku Tamu &amp; Ucapan</p>
      <form onSubmit={submit} className="rounded-2xl border border-white/15 bg-white/5 p-6 text-left backdrop-blur-md">
        <div className="space-y-3">
          <input data-testid="gb-nama" className={inputCls} placeholder="Nama kamu" value={nama} onChange={(e) => setNama(e.target.value)} />
          <textarea data-testid="gb-pesan" className={inputCls} rows={3} placeholder="Tulis ucapan & doa untuk kedua mempelai..." value={pesan} onChange={(e) => setPesan(e.target.value)} />
          <input data-testid="gb-captcha" className={inputCls} inputMode="numeric" placeholder={`Verifikasi: berapa ${c1} + ${c2}?`} value={cap} onChange={(e) => setCap(e.target.value)} />
          <button data-testid="gb-submit" type="submit" disabled={loading} style={{ backgroundColor: accent }} className="w-full rounded-full py-3 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {loading ? "Mengirim..." : "Kirim Ucapan"}
          </button>
        </div>
      </form>
      {list.length > 0 && (
        <div className="mt-6 space-y-3 text-left" data-testid="guestbook-wall">
          {list.map((g, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-md">
              <p className="text-sm leading-relaxed text-white/85">"{g.pesan}"</p>
              <p className="mt-2 text-xs uppercase tracking-wider" style={{ color: accent, fontFamily: font }}>— {g.nama}</p>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default function Invitation() {
  const { pid } = useParams();
  const [sp] = useSearchParams();
  const to = (sp.get("to") || "").slice(0, 60);
  const [inv, setInv] = useState(undefined);
  useEffect(() => {
    axios.get(`${API}/public/invitation/${pid}`).then((r) => setInv(r.data)).catch(() => setInv(null));
  }, [pid]);
  const cd = useCountdown(inv?.wedding_date);
  const [opened, setOpened] = useState(false);
  // If cover disabled by user, skip envelope screen automatically
  useEffect(() => {
    if (inv && inv.design && inv.design.cover && inv.design.cover.enabled === false) setOpened(true);
  }, [inv]);

  const openInvitation = () => { setOpened(true); };

  if (inv === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] font-heading text-2xl text-white/70">Memuat undangan…</div>;
  if (inv === null) return <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] font-heading text-2xl text-white/70">Undangan tidak ditemukan</div>;

  const tpl = getTemplate(inv.template);
  const accent = tpl.accent;
  const font = tpl.font;
  const design = mergeDesign(inv.design);
  const layout = inv.photo_layout || "background";
  const events = (inv.events && inv.events.length ? inv.events : [{ nama: "Resepsi", tanggal: inv.wedding_date, jam: inv.wedding_time, tempat: inv.venue_name, alamat: inv.venue_address, maps_url: inv.maps_url }]);
  const coverPhoto = pick(design.cover.photo, design.cover.photo_url) || inv.couple_photo;
  const pembukaBg = pick(design.pembuka.bg, design.pembuka.bg_url);
  const isBg = layout === "background" && inv.couple_photo;
  const bgImage = isBg ? inv.couple_photo : (getBackgroundUrl(inv.bg_theme) || null);
  const namaPria = design.cover.nama_pria;
  const namaWanita = design.cover.nama_wanita;
  const titleNames = (namaPria && namaWanita) ? `${namaPria} & ${namaWanita}` : inv.nama;
  const showTo = to || design.cover.default_to;
  const openLabel = design.cover.button_label || "Buka Undangan";
  const bgOpacity = Math.max(0.05, Math.min(0.95, (design.bg?.opacity ?? 40) / 100));

  return (
    <div className="relative min-h-screen overflow-hidden text-white" style={{ background: tpl.base }} data-testid="invitation-page">
      {opened && inv.music_url && (
        <MusicPlayer
          url={inv.music_url}
          startSeconds={inv.music_start_seconds || 0}
          color={accent}
          bg="rgba(0,0,0,0.5)"
          testid="standard-music-player"
        />
      )}


      <AnimatePresence>
        {!opened && design.cover.enabled && (
          <motion.div key="envelope" initial={{ opacity: 1 }} exit={{ opacity: 0, scale: 1.05 }} transition={{ duration: 0.8 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden px-6 text-center" style={{ background: tpl.bg || tpl.base }} data-testid="envelope-cover">
            {(coverPhoto || bgImage) && <img src={coverPhoto || bgImage} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ opacity: bgOpacity * 0.75 }} />}
            <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${tpl.base}c0, ${tpl.base}f0)` }} />
            <div className="grain-overlay absolute inset-0 opacity-[0.12]" />
            <TemplateDecor cat={tpl.cat} accent={accent} />
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }} className="relative">
              <MailOpen className="mx-auto h-12 w-12" style={{ color: accent }} />
              <p className="mt-6 text-xs uppercase tracking-[0.4em] text-white/60">{design.cover.eyebrow_top || "The Wedding Of"}</p>
              <h1 className="mt-3 text-5xl font-light tracking-tight sm:text-6xl" style={{ fontFamily: font }}>{titleNames}</h1>
              {design.cover.eyebrow_bottom && <p className="mt-3 text-sm uppercase tracking-[0.3em] text-white/70">{design.cover.eyebrow_bottom}</p>}
              {design.cover.subline && <p className="mt-4 text-sm text-white/70">{design.cover.subline}</p>}
              {showTo && <p className="mt-4 text-sm text-white/70">Kepada Yth. <span className="font-medium">{showTo}</span></p>}
              <button data-testid="open-invitation" onClick={openInvitation} style={{ backgroundColor: accent }}
                className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-black transition-transform hover:-translate-y-1 active:scale-95">
                <MailOpen className="h-4 w-4" /> {openLabel}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* legacy music toggle removed — MusicPlayer handles play/pause */}

      {bgImage ? (
        <motion.img src={bgImage} alt="" initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 h-full w-full object-cover" style={{ opacity: bgOpacity }} />
      ) : (
        <div className="absolute inset-0" style={{ background: tpl.bg || `radial-gradient(60% 50% at 50% 20%, ${accent}22 0%, transparent 70%)` }} />
      )}
      <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${tpl.base}b0, ${tpl.base}70, ${tpl.base}f0)` }} />
      <div className="grain-overlay absolute inset-0 opacity-[0.12]" />
      <TemplateDecor cat={tpl.cat} accent={accent} />

      {/* Desktop-wide layout: on md+ splits into fixed left photo + right scrolling content */}
      <div className="relative flex min-h-screen flex-col md:flex-row">
        {/* LEFT — dominant couple photo (desktop only). Mobile falls back to
             the background image behind content. */}
        <aside className="relative hidden md:sticky md:top-0 md:block md:h-screen md:w-[46%] md:flex-shrink-0"
          data-testid="invitation-photo-panel">
          {(inv.couple_photo || bgImage) && (
            <img src={inv.couple_photo || bgImage} alt=""
              className="absolute inset-0 h-full w-full object-cover" />
          )}
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${tpl.base}30, ${tpl.base}70)` }} />
          <div className="absolute inset-0 flex flex-col justify-end p-10 text-white">
            <p className="text-[10px] uppercase tracking-[0.6em]" style={{ color: accent }}>
              {design.cover.eyebrow_top || "The Wedding Of"}
            </p>
            <h1 className="mt-3 text-5xl font-light leading-tight tracking-tight lg:text-6xl" style={{ fontFamily: font }}>
              {titleNames}
            </h1>
            {inv.wedding_date && (
              <p className="mt-4 text-xs uppercase tracking-[0.4em] text-white/80">{formatDate(inv.wedding_date)}</p>
            )}
          </div>
        </aside>

        <div className="relative mx-auto flex min-h-screen w-full max-w-3xl flex-col items-center px-6 py-16 text-center md:w-[54%] md:px-10">
        {showTo && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-4 rounded-full border border-white/15 bg-white/5 px-5 py-2 backdrop-blur-md" data-testid="guest-greeting">
            <span className="text-xs uppercase tracking-[0.2em] text-white/60">Kepada Yth.</span>
            <span className="ml-2 text-sm font-medium">{showTo}</span>
          </motion.div>
        )}

        {/* PEMBUKA */}
        {design.pembuka.enabled && (design.pembuka.text || design.pembuka.quote || design.pembuka.intro) && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="relative mb-6 w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md" data-testid="pembuka-section">
            {pembukaBg && <img src={pembukaBg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
            <div className="relative">
              {design.pembuka.text && <p className="text-xs uppercase tracking-[0.3em] text-white/60">{design.pembuka.text}</p>}
              {design.pembuka.quote && <p className="mt-3 text-lg font-light italic leading-relaxed text-white/90" style={{ fontFamily: font }}>"{design.pembuka.quote}"</p>}
              {design.pembuka.quote_source && <p className="mt-2 text-xs uppercase tracking-wider" style={{ color: accent }}>— {design.pembuka.quote_source}</p>}
              {design.pembuka.intro && <p className="mt-4 text-sm leading-relaxed text-white/75">{design.pembuka.intro}</p>}
            </div>
          </motion.div>
        )}

        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-xs uppercase tracking-[0.4em] text-white/60">{design.cover.eyebrow_top || "The Wedding Of"}</motion.p>

        {(layout === "top" || layout === "circle") && inv.couple_photo && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }} className="mt-6 w-full" data-testid="photo-top">
            <PhotoBlock src={inv.couple_photo} shape={layout} />
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className="mt-6">
          <div className="flex items-center justify-center">
            <Heart className="h-10 w-10" style={{ color: accent, fill: accent }} />
          </div>
          <h1 className="mt-4 text-5xl font-light tracking-tight sm:text-7xl" style={{ fontFamily: font }}>{titleNames}</h1>
          {inv.wedding_date && <p className="mt-3 text-sm uppercase tracking-[0.2em] text-white/70">{formatDate(inv.wedding_date)}{inv.wedding_time ? ` · ${inv.wedding_time} WIB` : ""}</p>}
        </motion.div>

        {inv.greeting && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 max-w-xl text-xl font-light italic leading-relaxed text-white/80" style={{ fontFamily: font }}>"{inv.greeting}"</motion.p>
        )}

        {/* MEMPELAI */}
        {(design.pria.nama_lengkap || design.wanita.nama_lengkap || pick(design.pria.foto, design.pria.foto_url) || pick(design.wanita.foto, design.wanita.foto_url)) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-12 grid w-full max-w-2xl gap-4 sm:grid-cols-2" data-testid="mempelai-section">
            <BrideGroomCard side="pria" data={design.pria} accent={accent} font={font} label="Mempelai Pria" />
            <BrideGroomCard side="wanita" data={design.wanita} accent={accent} font={font} label="Mempelai Wanita" />
          </motion.div>
        )}

        {/* COUNTDOWN */}
        {design.countdown.enabled && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-12 flex flex-col items-center gap-4" data-testid="countdown-section">
            {design.countdown.label_atas && <p className="text-xs uppercase tracking-[0.3em] text-white/60">{design.countdown.label_atas}</p>}
            <div className="flex items-center gap-3 sm:gap-5">
              {inv.wedding_date ? (
                cd.done ? <p className="text-3xl" style={{ fontFamily: font }}>Hari yang dinanti telah tiba!</p> : (
                  <>
                    <Unit value={cd.d} label="Hari" accent={accent} font={font} /><Unit value={cd.h} label="Jam" accent={accent} font={font} /><Unit value={cd.m} label="Menit" accent={accent} font={font} /><Unit value={cd.s} label="Detik" accent={accent} font={font} />
                  </>
                )
              ) : <p className="text-white/60">Tanggal akan diumumkan</p>}
            </div>
            {inv.wedding_date && (
              <button data-testid="add-to-calendar" onClick={() => downloadICS({ title: `Wedding ${titleNames}`, dateStr: inv.wedding_date, timeStr: inv.wedding_time, location: events[0]?.tempat })} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs hover:bg-white/10" style={{ borderColor: `${accent}66`, color: accent }}>
                <CalendarDays className="h-3.5 w-3.5" /> {design.countdown.calendar_label || "Simpan ke Kalender"}
              </button>
            )}
          </motion.div>
        )}

        {/* ACARA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="mt-14 grid w-full max-w-md gap-4" data-testid="events-section">
          {events.map((ev, i) => (
            (ev.nama || ev.tempat || ev.alamat || ev.tanggal) ? (
              <div key={i} data-testid={`event-card-${i}`} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
                <CalendarDays className="mx-auto h-6 w-6" style={{ color: accent }} />
                {ev.nama && <p className="mt-2 text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>{ev.nama}</p>}
                {ev.tanggal && <p className="mt-2 text-sm text-white/80">{formatDate(ev.tanggal)}{ev.jam ? ` · ${ev.jam} WIB` : ""}</p>}
                {ev.tempat && <p className="mt-2 text-2xl" style={{ fontFamily: font }}>{ev.tempat}</p>}
                {ev.dresscode && <p className="mt-1 text-xs uppercase tracking-wider text-white/60">Dress Code: {ev.dresscode}</p>}
                {ev.alamat && <p className="mt-1 text-sm text-white/60">{ev.alamat}</p>}
                {ev.maps_url && <a href={ev.maps_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 rounded-full border px-4 py-2 text-xs hover:bg-white/10" style={{ borderColor: `${accent}66` }}><ExternalLink className="h-3 w-3" /> Lihat Peta</a>}
              </div>
            ) : null
          ))}
        </motion.div>

        {/* KISAH KAMI */}
        {design.kisah.enabled && design.kisah.items.some((it) => it.judul || it.cerita || it.tahun) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-14 w-full max-w-md" data-testid="kisah-section">
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-white/60">Kisah Kami</p>
            <div className="space-y-4">
              {design.kisah.items.filter((it) => it.judul || it.cerita || it.tahun).map((it, i) => (
                <div key={i} data-testid={`kisah-item-${i}`} className="rounded-xl border-l-2 border-white/20 bg-white/5 p-4 text-left backdrop-blur-md" style={{ borderLeftColor: accent }}>
                  {it.tahun && <p className="text-xs uppercase tracking-wider" style={{ color: accent }}>{it.tahun}</p>}
                  {it.judul && <p className="mt-1 text-lg" style={{ fontFamily: font }}>{it.judul}</p>}
                  {it.cerita && <p className="mt-1 text-sm text-white/75">{it.cerita}</p>}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {layout === "bottom" && inv.couple_photo && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-14 w-full max-w-md" data-testid="photo-bottom">
            <PhotoBlock src={inv.couple_photo} shape="bottom" />
          </motion.div>
        )}

        {/* GALERI */}
        {design.galeri.enabled && inv.gallery?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }} className="mt-14 w-full max-w-2xl" data-testid="invitation-gallery">
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-white/60">Galeri</p>
            <div className="columns-2 gap-3 sm:columns-3 [&>*]:mb-3">
              {inv.gallery.map((g, i) => (
                <img key={i} src={g} alt={`galeri-${i}`} loading="lazy"
                  className="w-full break-inside-avoid rounded-xl border border-white/10 object-cover" />
              ))}
            </div>
            {design.galeri.quote && <p className="mt-4 text-sm italic text-white/70" style={{ fontFamily: font }}>"{design.galeri.quote}"</p>}
            {design.galeri.quote_source && <p className="mt-1 text-xs uppercase tracking-wider" style={{ color: accent }}>— {design.galeri.quote_source}</p>}
          </motion.div>
        )}

        {/* WEDDING GIFT */}
        {design.gift.enabled && design.gift.accounts.some((a) => a.nomor || a.label) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="mt-14 w-full max-w-md" data-testid="gift-section">
            <p className="mb-3 text-xs uppercase tracking-[0.3em] text-white/60">Wedding Gift</p>
            {design.gift.intro && <p className="mb-4 text-sm text-white/70">{design.gift.intro}</p>}
            <div className="space-y-3">
              {design.gift.accounts.filter((a) => a.nomor || a.label).map((a, i) => (
                <div key={i} data-testid={`gift-account-${i}`}><GiftAccount acc={a} accent={accent} /></div>
              ))}
            </div>
          </motion.div>
        )}

        {inv.rundown?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }} className="mt-14 w-full max-w-md">
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-white/60">Rundown Acara</p>
            <div className="space-y-3">
              {inv.rundown.map((r, i) => (
                <div key={i} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left backdrop-blur-md">
                  <span className="text-lg text-white/90" style={{ fontFamily: font }}>{r.jam || "--:--"}</span>
                  <div><p className="text-sm font-medium">{r.kegiatan}</p>{r.keterangan && <p className="text-xs text-white/50">{r.keterangan}</p>}</div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* RSVP */}
        {design.rsvp.enabled && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }} className="mt-14 w-full max-w-md">
            <RSVPForm pid={pid} accent={accent} initialName={to} cfg={design.rsvp} events={events} />
          </motion.div>
        )}

        {/* UCAPAN (Guestbook) */}
        {design.ucapan.enabled && <GuestbookSection pid={pid} accent={accent} font={font} />}

        {/* PENUTUP */}
        {design.penutup.enabled && (design.penutup.pesan || design.penutup.ttd_pria || design.penutup.ttd_wanita) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }} className="mt-14 w-full max-w-2xl rounded-2xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-md" data-testid="penutup-section">
            {design.penutup.judul && <p className="text-2xl" style={{ fontFamily: font, color: accent }}>{design.penutup.judul}</p>}
            {design.penutup.pesan && <p className="mt-4 text-sm leading-relaxed text-white/80">{design.penutup.pesan}</p>}
            {(design.penutup.ttd_pria || design.penutup.ttd_wanita) && (
              <div className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
                {design.penutup.ttd_pria && <div><p className="text-xs uppercase tracking-wider text-white/60">Keluarga Pria</p><p className="mt-1" style={{ fontFamily: font, color: accent }}>{design.penutup.ttd_pria}</p></div>}
                {design.penutup.ttd_wanita && <div><p className="text-xs uppercase tracking-wider text-white/60">Keluarga Wanita</p><p className="mt-1" style={{ fontFamily: font, color: accent }}>{design.penutup.ttd_wanita}</p></div>}
              </div>
            )}
          </motion.div>
        )}

        <div className="mt-16 flex items-center gap-2 text-xs text-white/50">
          build with love <Heart className="h-3.5 w-3.5" style={{ color: accent, fill: accent }} /> · Wedifylab
        </div>
        </div>
      </div>
    </div>
  );
}
